// src/pages/analytics/SalesTrendsTab.tsx
//
// Sales Trends — bucketed sales time series (recipe: sales-trend). Bucket
// selector pills (week/month/quarter/year/season), optional date range, the
// shared customer/item filter bar, a recharts bar chart of dollars by period
// (same recharts + dark-slate styling as Dashboard.tsx), full table below,
// CSV export.
//
// periodStart/label are plain strings from the agent — rendered as-is.
//
// Calendar (Gregorian | Hebrew) shows with the filter pane flag
// (analytics.filterpane, agent 3.20.0+); default = tenant's
// analytics.default_calendar. Season is always Gregorian.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart as LineChartIcon, RefreshCw, Download, AlertTriangle, FileSpreadsheet, RotateCcw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useAuthContext } from '../../hooks/useAuth';
import { adminApi } from '../../services/adminApi';
import { apiV2 as api } from '../../services/apiV2';
import DateField from '../../components/DateField';
import type { SalesTrendBucket, SalesTrendPoint } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { SalesFilterBar, EMPTY_SALES_FILTERS, salesFilterParams, type SharedSalesFilterState } from './salesFilterBar';
import { FilterPane, AnalyticsSavedViews } from './filterPane';
import { useFilterPane } from './filterPaneState';

type TrendMeasure = 'dollars' | 'qty';
const MEASURES: { value: TrendMeasure; label: string }[] = [
  { value: 'dollars', label: 'Dollars' },
  { value: 'qty',     label: 'Qty' },
];

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

const BUCKETS: { value: SalesTrendBucket; label: string }[] = [
  { value: 'week',    label: 'Week' },
  { value: 'month',   label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year',    label: 'Year' },
  { value: 'season',  label: 'Season' },
];

function trendCols(): CsvColumns<SalesTrendPoint> {
  return [
    ['Period Start', (r) => r.periodStart],
    ['Period',       (r) => r.label],
    ['Qty',          (r) => r.qty],
    ['Dollars',      (r) => r.dollars],
    ['Invoices',     (r) => r.invoiceCount],
    ['Customers',    (r) => r.customerCount],
    ['Items',        (r) => r.itemCount],
  ];
}
const exportTrendCsv  = (rows: SalesTrendPoint[]) => exportCsv('sales-trend', trendCols(), rows);
const exportTrendXlsx = (rows: SalesTrendPoint[]) => exportXlsx('sales-trend', trendCols(), rows);

export function SalesTrendsTab() {
  const [bucket, setBucket] = useState<SalesTrendBucket>('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [measure, setMeasure] = useState<TrendMeasure>('dollars');
  const [shared, setShared] = useState<SharedSalesFilterState>(EMPTY_SALES_FILTERS);
  const pane = useFilterPane();
  const isQty = measure === 'qty';
  const { claims, activeTenant } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;
  const [calendarOverride, setCalendarOverride] = useState<string | null>(null);
  // Same tenant default-calendar lookup (and cache key) as the YoY tab.
  const calendarDefaultQ = useQuery({
    queryKey: ['analyticsYoy', 'default-calendar', slug],
    queryFn: () => adminApi.getConfig('analytics.default_calendar', slug ?? undefined),
    enabled: !!slug && pane.enabled,
    staleTime: 60 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
  const calendar = !pane.enabled || bucket === 'season'
    ? 'gregorian'
    : calendarOverride ?? (calendarDefaultQ.data?.value === 'hebrew' ? 'hebrew' : 'gregorian');

  // Reset every control to its initial default.
  const resetFilters = () => {
    setBucket('month');
    setDateFrom('');
    setDateTo('');
    setMeasure('dollars');
    setShared(EMPTY_SALES_FILTERS);
    setCalendarOverride(null);
  };

  const { data: filters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
  });

  const q = useQuery({
    queryKey: ['sales-trend', bucket, calendar, dateFrom, dateTo, shared, pane.key],
    queryFn: () => api.getSalesTrendSeries({
      bucket,
      calendar: calendar === 'hebrew' ? 'hebrew' : undefined,
      dateFrom: dateFrom || undefined,
      dateTo:   dateTo || undefined,
      ...salesFilterParams(shared),
      ...pane.params,
    }),
    staleTime: 60_000,
    retry: 1,
  });

  const series = q.data?.series ?? [];
  const totals = q.data?.totals;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <LineChartIcon size={20} /> Sales Trends
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Sales bucketed by week, month, quarter, year, or season — filtered by rep, customer, route, or item.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Bucket</span>
          <div className="flex items-center gap-1.5">
            {BUCKETS.map(b => (
              <button key={b.value} onClick={() => setBucket(b.value)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  bucket === b.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {b.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Date Range</span>
          <div className="flex items-center gap-1.5">
            <DateField value={dateFrom} onChange={setDateFrom} className="w-[130px]" />
            <span className="text-slate-600 text-xs">→</span>
            <DateField value={dateTo} onChange={setDateTo} className="w-[130px]" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Measure</span>
          <div className="flex items-center gap-1.5">
            {MEASURES.map(m => (
              <button key={m.value} onClick={() => setMeasure(m.value)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  measure === m.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        {pane.enabled && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Calendar</span>
            <div className="flex items-center gap-1.5">
              {(['gregorian', 'hebrew'] as const).map((c) => (
                <button key={c} onClick={() => setCalendarOverride(c)} disabled={bucket === 'season'}
                  title={bucket === 'season' ? 'Seasons are Gregorian' : undefined}
                  className={`px-2 py-1.5 text-[11px] rounded-md border transition-all capitalize disabled:opacity-40 ${
                    calendar === c
                      ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                      : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                  }`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}
        <SalesFilterBar value={shared} onChange={setShared} filters={filters} />
        <div className="flex-1" />
        {pane.enabled && (
          <AnalyticsSavedViews
            page="sales-trend"
            getState={() => ({ bucket, dateFrom, dateTo, measure, shared, calendar: calendarOverride })}
            applyState={(s) => {
              if (typeof s.bucket === 'string' && BUCKETS.some(b => b.value === s.bucket)) setBucket(s.bucket as SalesTrendBucket);
              if (typeof s.dateFrom === 'string') setDateFrom(s.dateFrom);
              if (typeof s.dateTo === 'string') setDateTo(s.dateTo);
              if (s.measure === 'dollars' || s.measure === 'qty') setMeasure(s.measure);
              if (s.shared && typeof s.shared === 'object') setShared({ ...EMPTY_SALES_FILTERS, ...(s.shared as Partial<SharedSalesFilterState>) });
              setCalendarOverride(s.calendar === 'hebrew' || s.calendar === 'gregorian' ? s.calendar : null);
            }}
          />
        )}
        <button
          onClick={resetFilters}
          title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => exportTrendCsv(series)}
          disabled={series.length === 0}
          title="Export the trend table to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => exportTrendXlsx(series)}
          disabled={series.length === 0}
          title="Export the trend table to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Totals */}
      {totals && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Total $</p><p className="text-lg font-bold text-white">{fmtCur(totals.dollars)}</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Total Qty</p><p className="text-lg font-bold text-white">{fmtNum(totals.qty)}</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Periods</p><p className="text-lg font-bold text-white">{fmtNum(totals.periods)}</p>
          </div>
        </div>
      )}

      <FilterPane />

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the trend</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && (
        <>
          {/* Chart — dollars by period. Single series, so identity lives in the
              title; hover tooltip carries the detail. */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <h3 className="text-sm font-semibold text-white mb-3">{isQty ? 'Qty' : 'Sales $'} by {bucket}</h3>
            {series.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No sales in the selected range</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      interval="preserveStartEnd" minTickGap={24} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => isQty ? fmtNum(Number(v)) : `$${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip
                      cursor={{ fill: '#475569', fillOpacity: 0.15 }}
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                      formatter={(value) => [isQty ? fmtNum(Number(value)) : fmtCur(Number(value)), isQty ? 'Qty' : 'Sales']}
                    />
                    <Bar dataKey={isQty ? 'qty' : 'dollars'} fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Period</th>
                    <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Start</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Qty</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">$</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Invoices</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Customers</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Items</th>
                  </tr>
                </thead>
                <tbody>
                  {series.map(p => (
                    <tr key={p.periodStart} className="border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                      <td className="px-3 py-2 text-white">{p.label}</td>
                      <td className="px-3 py-2 text-slate-500 font-mono">{p.periodStart}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{fmtNum(p.qty)}</td>
                      <td className="px-3 py-2 text-right text-slate-300">{fmtCur(p.dollars)}</td>
                      <td className="px-3 py-2 text-right text-slate-400">{fmtNum(p.invoiceCount)}</td>
                      <td className="px-3 py-2 text-right text-slate-400">{fmtNum(p.customerCount)}</td>
                      <td className="px-3 py-2 text-right text-slate-400">{fmtNum(p.itemCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {series.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">No periods to show</div>
            )}
            <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
              {series.length} period{series.length === 1 ? '' : 's'}
              {q.data?.dateFrom && q.data?.dateTo && ` · ${q.data.dateFrom} → ${q.data.dateTo}`}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
